import * as React from "react";

// material-ui
import makeStyles from "@material-ui/styles/makeStyles";

import Drawer from "@material-ui/core/Drawer";
import Typography from "@material-ui/core/Typography";

import Divider from "@material-ui/core/Divider";

//icons
import IconButton from "@material-ui/core/IconButton";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

// jaza-soft
import Table from "jazasoft/lib/mui/components/Table";
import MuiTable from "../../components/MuiTable";

import { isEmpty, isTrue, toFixedValue } from "../../utils/helpers";
import { useDeepMemo } from "../../hooks";
import { toFixed, toFixedSigned } from "../../utils/helpers";
import { getGroupName } from "./groupingHelper";

const shrinkageRange = (group, key) => {
  let result;
  if (group[`${key}Axis`] !== undefined && group[`${key}Variance`] !== undefined) {
    result = `(${toFixedSigned(group[`${key}Axis`], 1)}% to ${toFixedSigned(group[`${key}Axis`] + group[`${key}Variance`], 1)}%)`;
  }
  return result;
};

const useStyles = makeStyles(theme => ({
  drawerPaper: {
    maxWidth: 1000,
    minWidth: 800
  },
  drawerHeader: {
    display: "flex",
    width: props => props.drawerWidth || 500,
    alignItems: "center",
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-start"
  },
  table: {
    whiteSpace: "pre",
    "& th, & td": {
      border: "1px solid rgba(224, 224, 224, 1)"
    }
  }
}));

const RollDrawer = props => {
  const classes = useStyles();
  const { open, type, group, groupList = [], rollInfoList = [], onDrawerClose, flatView } = props;

  const columns = useDeepMemo(() => {
    let columns = [];
    let isKnitwear = false;
    if (type === "deviation" && group) {
      isKnitwear = group.deviatedRolls?.some(ri => !isEmpty(ri.weight) && ri.weight > 0);
      columns = [
        { dataKey: "serialNo", title: "Sl. No.", align: "center" },
        { dataKey: "rollNo", title: "F. Roll No", align: "center" },
        { dataKey: "color", title: "Style Color", align: "center" },
        { dataKey: "length", title: "Length", align: "center", render: value => (isEmpty(value) ? "-" : `${toFixedValue(value, 2)} m`) },
        { dataKey: "weight", title: "Weight", align: "center", render: value => (isEmpty(value) ? "-" : `${toFixedValue(value, 3)} kg`) },
        { dataKey: "warp", title: `Warp ${shrinkageRange(group, "warp")}`, align: "center" },
        { dataKey: "weft", title: `Weft ${shrinkageRange(group, "weft")}`, align: "center" }
      ];
    } else if (type === "rollInfo" && !isEmpty(groupList)) {
      isKnitwear = groupList?.flatMap(g => g.rollList || g.rollInfoList || []).some(ri => !isEmpty(ri.weight) && ri.weight > 0);
      columns = [
        {
          dataKey: "group",
          title: "Group",
          rowSpan: ({ record }) => (record.serialNo === 1 ? record.rollCount : 0),
          align: "center"
        },
        {
          dataKey: "interval",
          title: "Interval",
          rowSpan: ({ record }) => (record.serialNo === 1 ? record.rollCount : 0)
        },
        { dataKey: "serialNo", title: "Sl. No.", align: "center" },
        { dataKey: "rollNo", title: "F. Roll No", align: "center" },
        { dataKey: "length", title: "Length", align: "center", render: value => (isEmpty(value) ? "-" : `${toFixedValue(value, 2)} m`) },
        { dataKey: "weight", title: "Weight", align: "center", render: value => (isEmpty(value) ? "-" : `${toFixedValue(value, 3)} kg`) },
        { dataKey: "warpShrinkage", title: "Warp \nShrinkage (%)", align: "center" },
        { dataKey: "weftShrinkage", title: "Weft \nShrinkage (%)", align: "center" }
      ];
    } else if (type === "flatView" && isTrue(flatView)) {
      isKnitwear = rollInfoList
        .filter(ri => !isEmpty(ri.finalGroupId) && ri.finalGroupId === group?.id)
        .some(ri => !isEmpty(ri.weight) && ri.weight > 0);
      columns = [
        { dataKey: "rollNo", title: "F. Roll No." },
        { dataKey: "supplierRollNo", title: "S. Roll No." },
        { dataKey: "style", title: "Style", filterOptions: { filter: true } },
        { dataKey: "color", title: "Color", filterOptions: { filter: true } },
        { dataKey: "length", title: "Length", render: value => (isEmpty(value) ? "-" : `${toFixedValue(value, 2)} m`) },
        { dataKey: "weight", title: "Weight", align: "center", render: value => (isEmpty(value) ? "-" : `${toFixedValue(value, 3)} kg`) },
        { dataKey: "rollForm", title: "Roll Form" },
        {
          dataKey: "width",
          title: "Width",
          filterOptions: { filter: true },
          render: value => (isEmpty(value) ? "-" : `${toFixedValue(value, 1)} cm`)
        },
        { dataKey: "shade", title: "Shade", filterOptions: { filter: true } },
        { dataKey: "csv", title: "CSV" },
        { dataKey: "warpShrPercent", title: "Length \nShrinkage (%)", type: "double" },
        { dataKey: "weftShrPercent", title: "Width \nShrinkage (%)", type: "double" },
        { dataKey: "patternValues", title: "Pattern Values", filterOptions: { filter: true } },
        { dataKey: "patternNo", title: "Pattern No", filterOptions: { filter: true } },
        { dataKey: "returnStatus", title: "Return \nStatus", filterOptions: { filter: true } },
        { dataKey: "remarks", title: "Remarks" }
      ];
    }
    if (!isKnitwear) {
      columns = columns.filter(c => c.dataKey !== "weight");
    }
    return columns;
  }, [type, group, groupList, rollInfoList]);

  const rows = useDeepMemo(() => {
    let rows = [];
    if (type === "deviation" && group && group.deviatedRolls) {
      rows = group.deviatedRolls.map(({ warpShrinkage, weftShrinkage, ...rollInfo }, idx) => {
        let warp = [<span key={1}>{toFixedSigned(warpShrinkage, 1)}%</span>];
        let weft = [<span key={1}>{toFixedSigned(weftShrinkage, 1)}%</span>];
        if (warpShrinkage < group.warpAxis || warpShrinkage > toFixed(group.warpAxis + group.warpVariance)) {
          let d = warpShrinkage < group.warpAxis ? group.warpAxis - warpShrinkage : warpShrinkage - group.warpAxis - group.warpVariance;
          warp.push(<span key={2}> ({toFixed(d, 1)}%)</span>);
        }
        if (weftShrinkage < group.weftAxis || weftShrinkage > toFixed(group.weftAxis + group.weftVariance)) {
          let d = weftShrinkage < group.weftAxis ? group.weftAxis - weftShrinkage : weftShrinkage - group.weftAxis - group.weftVariance;
          weft.push(<span key={2}> ({toFixed(d, 1)}%)</span>);
        }
        return {
          ...rollInfo,
          length: `${toFixedValue(rollInfo.length)} m`,
          weight: !isEmpty(rollInfo.weight) ? `${toFixedValue(rollInfo.weight, 3)} kg` : null,
          serialNo: idx + 1,
          warp: <div style={{ color: warp.length === 2 ? "red" : null }}>{warp}</div>,
          weft: <div style={{ color: weft.length === 2 ? "red" : null }}>{weft}</div>
        };
      });
    } else if (type === "rollInfo" && !isEmpty(groupList)) {
      groupList.forEach((group, idx1) => {
        group.rollList.forEach(({ rollNo, length, weight, warpShrinkage, weftShrinkage }, idx2) => {
          let interval = (
            <div>
              <div key={1} style={{ display: "flex", justifyContent: "space-between" }}>
                <div>Warp:</div>
                <div>
                  ({toFixedSigned(group.warpAxis, 1)}% to {toFixedSigned(group.warpAxis + group.warpVariance, 1)}%)
                </div>
              </div>
              <div key={2} style={{ display: "flex", justifyContent: "space-between" }}>
                <div>Weft:</div>
                <div>
                  ({toFixedSigned(group.weftAxis, 1)}% to {toFixedSigned(group.weftAxis + group.weftVariance, 1)}%)
                </div>
              </div>
            </div>
          );
          rows.push({
            group: `G${idx1 + 1}`,
            interval,
            serialNo: idx2 + 1,
            rollNo,
            length: `${toFixedValue(length)} m`,
            weight: !isEmpty(weight) ? `${toFixedValue(weight, 3)} kg` : null,
            warpShrinkage: `${toFixedSigned(warpShrinkage, 1)}%`,
            weftShrinkage: `${toFixedSigned(weftShrinkage, 1)}%`,
            rollCount: group.rollList.length
          });
        });
      });
    } else if (type === "flatView" && isTrue(flatView)) {
      rows = rollInfoList
        .filter(ri => !isEmpty(ri.finalGroupId) && ri.finalGroupId === group?.id)
        .map(e => {
          return {
            ...e,
            patternValues:
              !isEmpty(group?.warpPattern) && !isEmpty(group?.weftPattern)
                ? "L=" + toFixedSigned(group.warpPattern, 1) + "%, W=" + toFixedSigned(group.weftPattern, 1) + "%"
                : "",
            patternNo: group?.patternNumber
          };
        });
    }
    return rows;
  }, [type, group, groupList, rollInfoList]);

  return (
    <div>
      {/** Drawer For Supplier Rolls Upload Against Fabric Item */}
      <Drawer anchor="right" classes={{ paper: classes.drawerPaper }} open={open} onClose={onDrawerClose}>
        <div className={classes.drawerHeader}>
          <IconButton onClick={onDrawerClose}>
            <ChevronRightIcon />
          </IconButton>
          <div>
            <Typography variant="h5">{type === "flatView" ? (isEmpty(group) ? "" : `${getGroupName(group)}`) : "Rolls"}</Typography>
          </div>
        </div>
        <Divider />
        <div>
          {!flatView && <Table classes={classes} columns={columns} rows={rows} />}
          {flatView && (
            <MuiTable
              classes={classes}
              columns={columns}
              rows={rows}
              searchable={true}
              pageable={true}
              searchKeys={["rollNo", "supplierRollNo"]}
              title="Rolls"
            />
          )}
        </div>
      </Drawer>
    </div>
  );
};

export default RollDrawer;
